import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DivTableSkeleton, { DivTableColumn } from '../skeleton/DivTableSkeleton';
import { formatLocalTime } from 'utils/formatDate';
import { ExternalLinkIcon } from '../icons/ExternalLinkIcon';
import styles from './TransactionTable.module.scss';

export interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  methodImageUrl?: string;
  cryptoName?: string;
  externalTxId?: string;
  explorerUrl?: string;
  networkName?: string;
  txHash?: string;
  method?: {
    name: string;
    imageUrl: string;
  };
  tipType?: 'sent' | 'received';
  tipUser?: string;
  direction?: 'sent' | 'received';
  counterpartyUsername?: string;
  currencyCode?: string;
}

interface TransactionTableProps {
  transactions: Transaction[];
  loading: boolean;
  activeTab?: 'Deposit' | 'Withdrawal' | 'Tip';
  onCancelClick?: (id: string) => void;
  isCancellingId?: string | null;
}

interface DynamicColumn {
  id: string;
  header: string;
  gridFr: string;
  skeletonWidth: string;
  headerClassName?: string;
  cellClassName?: string;
  render: (tx: Transaction) => React.ReactNode;
}

export const TransactionTable: FC<TransactionTableProps> = ({
  transactions,
  loading,
  activeTab,
  onCancelClick,
  isCancellingId,
}) => {
  const { t } = useTranslation();

  const columns = useMemo<DynamicColumn[]>(() => {
    if (activeTab === 'Tip') {
      return [
        {
          id: 'type',
          header: t('transactions.columns.type', 'Type'),
          gridFr: '1fr',
          skeletonWidth: '20%',
          render: (tx) => {
            const isReceived = (tx.direction || tx.tipType) === 'received';
            return (
              <span className={`${styles.status} ${isReceived ? styles.received : styles.sent}`}>
                {isReceived
                  ? t('transactions.types.received', 'Received')
                  : t('transactions.types.sent', 'Sent')}
              </span>
            );
          },
        },
        {
          id: 'user',
          header: t('transactions.columns.user', 'User'),
          gridFr: '1.5fr',
          skeletonWidth: '30%',
          render: (tx) => tx.counterpartyUsername || tx.tipUser || '-',
        },
        {
          id: 'amount',
          header: t('transactions.columns.amount', 'Amount'),
          gridFr: '1fr',
          skeletonWidth: '25%',
          render: (tx) => {
            const isReceived = (tx.direction || tx.tipType) === 'received';
            return (
              <span className={isReceived ? styles.tipReceived : styles.tipSent}>
                ${tx.amount} {tx.currencyCode || tx.currency}
              </span>
            );
          },
        },
        {
          id: 'date',
          header: t('transactions.columns.date', 'Date'),
          gridFr: '1fr',
          skeletonWidth: '25%',
          render: (tx) => (
            <span className={styles.dateText}>
              {formatLocalTime(tx.completedAt || tx.createdAt)}
            </span>
          ),
        },
      ];
    }

    const baseCols: DynamicColumn[] = [
      {
        id: 'status',
        header: t('transactions.columns.status', 'Status'),
        gridFr: '0.8fr',
        skeletonWidth: '40%',
        render: (tx) => {
          const statusKey = tx.status ? tx.status.toLowerCase() : '';
          const translatedStatus =
            statusKey && ['pending', 'completed', 'cancelled'].includes(statusKey)
              ? t(`transactions.statuses.${statusKey}`, tx.status)
              : tx.status;
          return (
            <span className={`${styles.status} ${tx.status ? styles[tx.status.toLowerCase()] : ''}`}>
              {translatedStatus}
            </span>
          );
        },
      },
      {
        id: 'amount',
        header: t('transactions.columns.amount', 'Amount'),
        gridFr: '1fr',
        skeletonWidth: '40%',
        render: (tx) => <>${tx.amount}</>,
      },
      {
        id: 'method',
        header: t('transactions.columns.method', 'Method'),
        gridFr: '1.2fr',
        skeletonWidth: '50%',
        render: (tx) => {
          const imgUrl = tx.methodImageUrl || tx.method?.imageUrl;
          const methodName = tx.networkName || tx.cryptoName || tx.method?.name || 'method';
          return (
            <div className={styles.methodInfo}>
              {imgUrl && <img src={imgUrl} alt={methodName} />}
              <span className={styles.cryptoName}>{methodName}</span>
            </div>
          );
        },
      },
    ];

    const hasAnyLink = transactions.some((tx) => tx.externalTxId && tx.explorerUrl);
    if (!loading && hasAnyLink) {
      baseCols.push({
        id: 'link',
        header: t('transactions.columns.link', 'Link'),
        gridFr: '1fr',
        skeletonWidth: '40%',
        render: (tx) =>
          tx.externalTxId && tx.explorerUrl ? (
            <a
              href={tx.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.txLink}
              title={tx.externalTxId}
            >
              {`${tx.externalTxId.slice(0, 8)}...`}
              <ExternalLinkIcon
                width="14"
                height="14"
                style={{ marginLeft: 4, verticalAlign: 'middle', marginBottom: 2 }}
              />
            </a>
          ) : (
            '-'
          ),
      });
    }

    baseCols.push({
      id: 'date',
      header: t('transactions.columns.date', 'Date'),
      gridFr: '1.2fr',
      skeletonWidth: '50%',
      render: (tx) => (
        <span className={styles.dateText}>{formatLocalTime(tx.completedAt || tx.createdAt)}</span>
      ),
    });

    const hasCancellableItems = transactions.some((tx) => tx.status?.toLowerCase() === 'pending');
    if (!loading && activeTab === 'Withdrawal' && hasCancellableItems) {
      baseCols.push({
        id: 'action',
        header: t('transactions.columns.action', 'Action'),
        gridFr: '0.8fr',
        skeletonWidth: '30%',
        headerClassName: styles.thAction,
        cellClassName: styles.tdAction,
        render: (tx) =>
          tx.status?.toLowerCase() === 'pending' && onCancelClick ? (
            <button
              className={styles.cancelButton}
              onClick={() => onCancelClick(tx.id)}
              disabled={isCancellingId === tx.id}
            >
              {isCancellingId === tx.id
                ? t('transactions.actions.cancelling', 'Cancelling...')
                : t('transactions.actions.cancel', 'Cancel')}
            </button>
          ) : null,
      });
    }

    return baseCols;
  }, [activeTab, transactions, loading, onCancelClick, isCancellingId, t]);

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: columns.map((col) => col.gridFr).join(' '),
    }),
    [columns],
  );

  const skeletonColumns = useMemo<DivTableColumn[]>(
    () => columns.map((col) => ({ label: col.header, width: col.skeletonWidth })),
    [columns],
  );

  if (!loading && transactions.length === 0) {
    return <div className={styles.noDataWrapper}>{t('transactions.emptyState', 'No transactions found')}</div>;
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader} style={gridStyle}>
        {columns.map((col) => (
          <div key={col.id} className={`${styles.th} ${col.headerClassName || ''}`}>
            {col.header}
          </div>
        ))}
      </div>

      <div className={styles.tableBody}>
        {loading ? (
          <DivTableSkeleton
            rows={5}
            rowClassName={styles.tableRow}
            cellClassName={styles.td}
            labelClassName={styles.mobileLabel}
            columns={skeletonColumns}
          />
        ) : (
          transactions.map((tx) => (
            <div key={tx.id} className={styles.tableRow} style={gridStyle}>
              {columns.map((col) => (
                <div key={col.id} className={`${styles.td} ${col.cellClassName || ''}`}>
                  <span className={styles.mobileLabel}>{col.header}</span>
                  {col.render(tx)}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TransactionTable;
